<template>
  <v-container fluid class="pa-4">
    <v-card class="elevation-2">
      <v-toolbar color="red darken-2" dark dense>
        <v-icon left>warning</v-icon>
        <v-toolbar-title>Kritik Stok Takibi</v-toolbar-title>
        <v-spacer />
        <v-btn icon :loading="loading" @click="load">
          <v-icon>refresh</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-layout wrap align-center class="mb-3">
          <v-flex xs12 sm4 class="pr-2">
            <v-select
              v-model="selectedUserId"
              :items="userItems"
              item-text="text"
              item-value="value"
              label="Kullanıcı"
              prepend-icon="person"
              hide-details
              clearable
              @change="load"
            ></v-select>
          </v-flex>
          <v-flex xs12 sm4 class="pr-2">
            <v-text-field
              v-model="search"
              label="Ara"
              prepend-icon="search"
              single-line
              hide-details
              clearable
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm4>
            <v-checkbox
              v-model="onlyWarnings"
              label="Sadece uyarılı satırlar"
              prepend-icon="filter_list"
              hide-details
              class="mt-0"
            ></v-checkbox>
          </v-flex>
        </v-layout>

        <v-layout wrap class="inv-stats">
          <v-flex xs6 sm3 class="pr-2">
            <div class="inv-stat inv-stat--red">
              <div class="inv-stat-label">Stok yok</div>
              <div class="inv-stat-value">{{ counts.out }}</div>
            </div>
          </v-flex>
          <v-flex xs6 sm3 class="pr-2">
            <div class="inv-stat inv-stat--amber">
              <div class="inv-stat-label">Kritik stok altı</div>
              <div class="inv-stat-value">{{ counts.below }}</div>
            </div>
          </v-flex>
          <v-flex xs6 sm3 class="pr-2">
            <div class="inv-stat inv-stat--green">
              <div class="inv-stat-label">Yeterli</div>
              <div class="inv-stat-value">{{ counts.ok }}</div>
            </div>
          </v-flex>
          <v-flex xs6 sm3>
            <div class="inv-stat inv-stat--grey">
              <div class="inv-stat-label">Takip edilen satır</div>
              <div class="inv-stat-value">{{ lines.length }}</div>
            </div>
          </v-flex>
        </v-layout>

        <div v-if="!filteredLines.length && !loading" class="inv-empty">
          <v-icon large color="grey lighten-1">assignment_late</v-icon>
          <div>Kritik stok girilmiş ürün yok. Kullanıcılar kendi "Kritik Stok" ekranından eşik girmeli.</div>
        </div>

        <v-data-iterator
          v-else-if="filteredLines.length"
          :items="filteredLines"
          :loading="loading"
          :rows-per-page-items="[50, 100, 500, { text: 'Tümü', value: -1 }]"
          item-key="rowKey"
          expand
          content-tag="div"
        >
          <template v-slot:item="props">
            <div
              class="inv-card inv-card--clickable"
              :class="[cardClass(props.item.status), props.expanded ? 'cs-open' : '']"
              @click="props.expanded = !props.expanded"
            >
              <div class="inv-icon" :class="iconClass(props.item.status)">
                <v-icon color="white" small>{{ statusIcon(props.item.status) }}</v-icon>
              </div>

              <div class="inv-card-l">
                <div class="inv-head">
                  <span class="inv-title">{{ props.item.productName }}</span>
                  <span v-if="props.item.status" class="inv-chip" :class="chipClass(props.item.status)">
                    {{ props.item.statusName }}
                  </span>
                  <span class="inv-sub">{{ props.item.productSku || '-' }}</span>
                </div>

                <div class="inv-meta">
                  <v-icon x-small color="blue-grey lighten-2">person</v-icon>
                  {{ props.item.userName }}
                  <span class="inv-dot">·</span>
                  <v-icon x-small color="blue-grey lighten-2">flag</v-icon>
                  Kritik stok: {{ props.item.criticalQuantity }}
                </div>

                <div v-if="props.expanded" class="cs-detail" @click.stop>
                  <div v-if="!props.item.hasRecipe" class="inv-note">
                    Bu ürünün reçetesi tanımlı değil — kasadan yapılabilir adet hesaplanamıyor.
                  </div>
                  <table v-else class="critical-components">
                    <thead>
                      <tr>
                        <th>Alt kalem</th>
                        <th class="text-xs-right">Gereken (1 ürün için)</th>
                        <th class="text-xs-right">Kullanıcının kasasındaki</th>
                        <th class="text-xs-right">Bu kalemden çıkan</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="c in props.item.components" :key="c.inventoryItemId">
                        <td>
                          {{ c.inventoryItemLabel }}
                          <span v-if="c.isBottleneck" class="inv-chip inv-chip--red ml-1">sınırlayan</span>
                        </td>
                        <td class="text-xs-right">{{ c.requiredQuantity }}</td>
                        <td class="text-xs-right">{{ c.heldQuantity }}</td>
                        <td class="text-xs-right">{{ c.buildableQuantity }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div class="inv-card-r">
                <div class="inv-value">
                  <div class="inv-value-num">{{ props.item.hasRecipe ? props.item.buildableQuantity : '-' }}</div>
                  <div class="inv-value-label">yapılabilir</div>
                </div>
                <v-icon small color="blue-grey lighten-1" class="ml-3">
                  {{ props.expanded ? 'expand_less' : 'expand_more' }}
                </v-icon>
              </div>
            </div>
          </template>
        </v-data-iterator>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { userService } from "@/_services";

export default {
  data: () => ({
    loading: false,
    lines: [],
    users: [],
    selectedUserId: null,
    search: "",
    onlyWarnings: false,
  }),

  computed: {
    userItems() {
      return this.users.map((u) => ({
        value: u.id,
        text: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.username,
      }));
    },
    filteredLines() {
      const q = (this.search || "").toLocaleLowerCase("tr");
      return this.lines.filter((l) => {
        if (this.onlyWarnings && l.status !== 2 && l.status !== 3) return false;
        if (!q) return true;
        return [l.productName, l.productSku, l.userName]
          .filter(Boolean)
          .some((v) => v.toLocaleLowerCase("tr").includes(q));
      });
    },
    counts() {
      return {
        out: this.lines.filter((l) => l.status === 3).length,
        below: this.lines.filter((l) => l.status === 2).length,
        ok: this.lines.filter((l) => l.status === 1).length,
      };
    },
  },

  created() {
    this.loadUsers();
    this.load();
  },

  methods: {
    statusIcon(status) {
      if (status === 3) return "error";
      if (status === 2) return "warning";
      if (status === 1) return "check_circle";
      return "help_outline";
    },
    iconClass(status) {
      if (status === 3) return "inv-icon--red";
      if (status === 2) return "inv-icon--amber";
      if (status === 1) return "inv-icon--green";
      return "inv-icon--grey";
    },
    chipClass(status) {
      if (status === 3) return "inv-chip--red";
      if (status === 2) return "inv-chip--wait";
      if (status === 1) return "inv-chip--done";
      return "inv-chip--grey";
    },
    cardClass(status) {
      if (status === 3) return "inv-card--red";
      if (status === 2) return "inv-card--wait";
      return "";
    },
    async loadUsers() {
      try {
        this.users = (await userService.getAll()) || [];
      } catch (e) {
        this.users = [];
      }
    },
    async load() {
      this.loading = true;
      try {
        const data = await this.$store.dispatch("criticalStock/getAllCriticalStock", {
          userId: this.selectedUserId,
        });
        this.lines = (data || []).map((l) => ({ ...l, rowKey: `${l.userId}-${l.productId}` }));
      } catch (e) {
        this.$store.state.dialog = true;
        this.$store.state.message =
          "Kritik stok listesi yüklenemedi: " +
          ((e && e.response && e.response.data && e.response.data.message) || (e && e.message) || "");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.cs-open {
  align-items: flex-start;
}
.cs-detail {
  margin-top: 10px;
  cursor: default;
}
.critical-components { width: 100%; border-collapse: collapse; }
.critical-components th,
.critical-components td { padding: 4px 8px; font-size: 12px; border-bottom: 1px solid #e0e0e0; }
.critical-components th { color: rgba(0, 0, 0, 0.54); font-weight: 500; text-align: left; }
</style>
